import { useState, useCallback, useEffect, useRef } from "react";

/**
 * プログレスモーダルの管理 hook
 * プロセスベースの進捗管理（プロセスキーによる完了管理）
 */
export const useProgressModal = () => {
  // プログレスモーダルの開閉状態
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);

  // プロセス管理: プロセスキーの配列と完了したプロセスキーのセット
  const [processKeys, setProcessKeys] = useState<string[]>([]);
  const [completedProcessKeys, setCompletedProcessKeys] = useState<Set<string>>(new Set());

  // アニメーション用の進捗値
  const [animatedProgressValue, setAnimatedProgressValue] = useState(0);
  const animationRef = useRef<NodeJS.Timeout | null>(null);

  // エラーメッセージ管理
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * 実際の進捗値を計算（完了プロセス数 / 全プロセス数 * 100）
   */
  const actualProgressValue =
    processKeys.length > 0 ? Math.floor((completedProcessKeys.size / processKeys.length) * 100) : 0;

  /**
   * アニメーションで進捗値を徐々に増加させる
   */
  useEffect(() => {
    if (!isProgressModalOpen) {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      return undefined;
    }

    // 100%の場合は即座に設定
    if (actualProgressValue >= 100) {
      setAnimatedProgressValue(100);
      return undefined;
    }

    // アニメーションをクリア
    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    // 現在のアニメーション値から目標値まで徐々に増加
    const targetValue = actualProgressValue;
    const currentValue = animatedProgressValue;
    const diff = targetValue - currentValue;

    // targetValue < currentValueの場合は即座に目標値に更新
    if (diff < 0) {
      setAnimatedProgressValue(targetValue);
      return undefined;
    }

    // targetValue > currentValueの場合のみアニメーション
    const increment = Math.max(1, Math.floor(Math.abs(diff) / 20)); // 20ステップで到達

    animationRef.current = setInterval(() => {
      setAnimatedProgressValue((prev) => {
        const next = prev + increment;
        if (next >= targetValue) {
          if (animationRef.current) {
            clearInterval(animationRef.current);
            animationRef.current = null;
          }
          return targetValue;
        }
        return next;
      });
    }, 50); // 50ms間隔

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [actualProgressValue, animatedProgressValue, isProgressModalOpen]);

  /**
   * プログレスモーダルを開く
   * @param processKeys - プロセスキーの配列（例: ['init', 'character', 'license']）
   */
  const openProgressModal = useCallback((processKeys: string[]) => {
    setIsProgressModalOpen(true);
    setProcessKeys(processKeys);
    setCompletedProcessKeys(new Set());
    setAnimatedProgressValue(0);
    setErrorMessage(null);
  }, []);

  /**
   * 指定したプロセスを完了状態にする
   * @param processKey - 完了するプロセスキー
   */
  const completeProcess = useCallback((processKey: string) => {
    setCompletedProcessKeys((prev) => new Set([...prev, processKey]));
  }, []);

  /**
   * プログレスモーダルを閉じる
   * プロセス情報をリセット
   */
  const closeProgressModal = useCallback(() => {
    setIsProgressModalOpen(false);
    setProcessKeys([]);
    setCompletedProcessKeys(new Set());
    setAnimatedProgressValue(0);
    setErrorMessage(null);
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  return {
    isProgressModalOpen,
    progressValue: animatedProgressValue, // アニメーションされた値を返す
    processKeys,
    completedProcessKeys,
    errorMessage,
    openProgressModal,
    closeProgressModal,
    completeProcess,
    setErrorMessage,
  };
};
